import { computed, ComputedRef, ref, Ref } from "vue"
import useAsclepius from "@common/composables/api/asclepius"
import {
  BenefitPlan,
  BenefitsApiListActiveEnrolleesByDemographicsRequest,
  BenefitsApiListBenefitPlansOfferedByCompanyBenefitRequest,
  BenefitsApiListCompanyBenefitPlanTierCostsRequest,
  BenefitsApiListOpenCompanyBenefitEnrollmentsForMemberRequest,
  CompanyBenefitEnrollment,
  CompanyBenefitPlanTierCost,
  ElectCompanyBenefitEnrollmentsRequest,
  ElectCompanyBenefitEnrollmentsResponse,
  ListActiveEnrolleesByDemographicsResponse,
  ListBenefitPlansOfferedByCompanyBenefitResponse,
  ListCompanyBenefitPlanTierCostsResponse,
  ListPendingCompanyBenefitEnrollmentsForMemberResponse,
  PendingCompanyBenefitEnrollmentDTO,
  RedactedCompanyBenefitEnrolleeDTO
} from "@vitable/asclepius-api-client-ts"

export function useListOpenCompanyBenefitEnrollmentsForMember() {
  const { getBenefitsApi } = useAsclepius()
  const enrollments: Ref<Array<CompanyBenefitEnrollment>> = ref([])

  async function listOpenCompanyBenefitEnrollmentsForMember(
    request: BenefitsApiListOpenCompanyBenefitEnrollmentsForMemberRequest
  ): Promise<Array<CompanyBenefitEnrollment>> {
    enrollments.value = (await getBenefitsApi().listOpenCompanyBenefitEnrollmentsForMember(request)).data.enrollments

    return enrollments.value
  }

  return {
    listOpenCompanyBenefitEnrollmentsForMember,
    enrollments
  }
}

export function useListCompanyBenefitPlans() {
  const { getBenefitsApi, callWithResponse, callResult } = useAsclepius()

  async function listCompanyBenefitPlans(
    request: BenefitsApiListBenefitPlansOfferedByCompanyBenefitRequest
  ): Promise<void> {
    await callWithResponse(() => getBenefitsApi().listBenefitPlansOfferedByCompanyBenefit(request))
  }

  const benefitPlans: ComputedRef<Array<BenefitPlan>> = computed(() => {
    const response: Nullable<ListBenefitPlansOfferedByCompanyBenefitResponse> = callResult.value.data
    return response?.benefit_plans ?? []
  })

  return {
    benefitPlans,
    listCompanyBenefitPlans,
    listCompanyBenefitPlansResult: callResult
  }
}

export function useListCompanyBenefitPlanTierCosts() {
  const { getBenefitsApi, callWithResponse, callResult } = useAsclepius()

  async function listCompanyBenefitPlanTierCosts(
    request: BenefitsApiListCompanyBenefitPlanTierCostsRequest
  ): Promise<void> {
    await callWithResponse(() => getBenefitsApi().listCompanyBenefitPlanTierCosts(request))
  }

  const companyBenefitPlanTierCosts: ComputedRef<Array<CompanyBenefitPlanTierCost>> = computed(() => {
    const response: Nullable<ListCompanyBenefitPlanTierCostsResponse> = callResult.value.data
    return response?.tier_costs ?? []
  })

  return {
    companyBenefitPlanTierCosts,
    listCompanyBenefitPlanTierCosts,
    listCompanyBenefitPlanTierCostsResult: callResult
  }
}

export function useListPendingCompanyBenefitEnrollments() {
  const { getBenefitsApi, callWithResponse, callResult } = useAsclepius()

  async function listPendingCompanyBenefitEnrollments(memberId: string): Promise<void> {
    await callWithResponse(() => getBenefitsApi().listPendingCompanyBenefitEnrollmentsForMember({ memberId }))
  }

  const pendingCompanyBenefitEnrollments: ComputedRef<Array<PendingCompanyBenefitEnrollmentDTO>> = computed(() => {
    const response: Nullable<ListPendingCompanyBenefitEnrollmentsForMemberResponse> = callResult.value.data
    return response?.pending_enrollments ?? []
  })

  return {
    pendingCompanyBenefitEnrollments,
    listPendingCompanyBenefitEnrollments,
    listPendingCompanyBenefitEnrollmentsResult: callResult
  }
}

export function useStartCompanyBenefitEnrollments() {
  const { getBenefitsApi, callWithResponse, callResult } = useAsclepius()

  async function startCompanyBenefitEnrollments(): Promise<void> {
    await callWithResponse(() => getBenefitsApi().startCompanyBenefitEnrollments())
  }

  return {
    startCompanyBenefitEnrollments,
    startCompanyBenefitEnrollmentsResult: callResult
  }
}

export function useListActiveEnrolleesByDemographics() {
  const { getBenefitsApi, callWithResponse, callResult } = useAsclepius()

  async function listActiveEnrolleesByDemographics(
    request: BenefitsApiListActiveEnrolleesByDemographicsRequest
  ): Promise<void> {
    await callWithResponse(() => getBenefitsApi().listActiveEnrolleesByDemographics(request))
  }

  const enrollees: ComputedRef<Array<RedactedCompanyBenefitEnrolleeDTO>> = computed(() => {
    const response: Nullable<ListActiveEnrolleesByDemographicsResponse> = callResult.value.data
    return response?.enrollees ?? []
  })

  return {
    listActiveEnrolleesByDemographics,
    listActiveEnrolleesByDemographicsResult: callResult,
    enrollees
  }
}

export function useElectCompanyBenefitEnrollments() {
  const { getBenefitsApi, callWithResponse, callResult } = useAsclepius()

  const electedCompanyBenefitEnrollments = ref<Array<CompanyBenefitEnrollment>>([])

  async function electCompanyBenefitEnrollments(request: ElectCompanyBenefitEnrollmentsRequest): Promise<void> {
    const data: ElectCompanyBenefitEnrollmentsResponse = await callWithResponse(() =>
      getBenefitsApi().electCompanyBenefitEnrollments({ electCompanyBenefitEnrollmentsRequest: request })
    )
    electedCompanyBenefitEnrollments.value = data.enrollments
  }

  return {
    electCompanyBenefitEnrollments,
    electCompanyBenefitEnrollmentsResult: callResult,
    electedCompanyBenefitEnrollments
  }
}
